import { SelectProps } from "@cofano/cck/Select/Select";
import { Select } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";
import { getCompatibleAgreementsByCharterType_query } from "view/agreements/utils/AgreementQueries";
import { AgreementType } from "view/agreementtypes/types/AgreementType";
import FavoriteSelectOption from "components/selects/FavoriteSelectOption";
import { useStoreState } from "ezStore";
import { buildAgreementOptions } from "view/agreements/utils/AgreementUtils";
import { AgreementTypeEnum, CharterSubtype, CharterType, FavoriteType } from "__generated__/enums";

type DefaultAgreementTypeSelectProps = {
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	agreementTypes?: AgreementType[];
	customAgreementTypes?: AgreementTypeEnum[];
}

const AgreementTypeSelect = <Multiple extends boolean = false,>({
	multiple,
	charterType,
	charterSubtype,
	agreementTypes,
	customAgreementTypes,
	value,
	onChange,
	getOptionDisabled,
}: Omit<SelectProps<AgreementTypeEnum, Multiple>, "fetchOptions"> & DefaultAgreementTypeSelectProps) => {
	const client = useApolloClient();
	const favorites = useStoreState(store => store.session.user?.properties.favorites) ?? [];

	const onFetchOptions = async() => {
		try {

			const result = await client.query({
				query: getCompatibleAgreementsByCharterType_query,
				variables: {
					charterType: charterType,
					charterSubtype: charterSubtype,
				},
			});

			const compatibleAgreementTypes = result.data.compatibleAgreementsByCharterType ?? [];

			const options = buildAgreementOptions({ compatibleAgreements: compatibleAgreementTypes, agreementTypes: agreementTypes ?? [], favorites, customAgreementTypes });

			return {
				options: options,
				totalResults: options.length,
			};
		} catch (ex) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			multiple={multiple}
			loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
			clearable={false}
			getOptionDisabled={getOptionDisabled}
			fetchOptions={onFetchOptions}
			getOptionLabel={(option) => intl.formatMessage({ id: `AGREEMENT_TYPE_${option}` })}
			renderOption={(props, option) => (
				<li {...props} key={option}>
					<FavoriteSelectOption
						entityId={agreementTypes?.find(x => x.type === option)?.id || 0}
						entityType={FavoriteType.AgreementType}>
						{intl.formatMessage({ id: `AGREEMENT_TYPE_${option}` })}
					</FavoriteSelectOption>
				</li>
			)}
			value={value}
			onChange={onChange}
		/>
	);
};

export default AgreementTypeSelect;
